import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import RisposteSelfAssessmentModal from '../../../components/km/RisposteSelfAssessmentModal.jsx';
import { useKmClient } from '../../../lib/useKmClient.js';
import { useKmQuery } from '../hooks/useKmQuery.js';
import { useAuth as useRealAuth } from '../../../context/AuthContext';
import { canEditScheda, canSalesMark } from '../../../lib/roles.js';
import ErrorDetails from '../components/ErrorDetails.jsx';
import { useFiltriEsperienze, nomeDaCodice } from '../hooks/useTassonomie.js';
import { etichettaUtilizzabile } from './EsperienzaDetailPage.jsx';
import { formatDataIt } from '../preavvisi.js';

/* Riscritto sul contratto reale (Collaudo_KM_TalentGPT_20260907_2.docx,
   §6.9): GET /schede/{id} risponde { id, esperienza_id, versione, sommario,
   blocco_a, blocco_b, blocco_c, nucleo_comune, stato_validazione, fonte,
   tecnologie_unificate: [], presenza_isa, citabilita, marcata_utilizzabile:
   bool, nota_sales, sommario_stato, ultima_compilazione_ts,
   ultima_compilazione_tipo, aggiornato_il, created_at, consulente_id,
   ruolo, stato_esperienza }.

   Scoperta dal vivo il 18/09 (React error #31, "object with keys..."):
   blocco_a/b/c NON sono testo semplice come assunto — sono OGGETTI con
   più campi dentro (es. { coordinamento, rischi_criticita,
   reporting_escalation }), diversi da blocco a blocco. Non documentato
   nel collaudo. renderBlocco() mostra ogni campo interno etichettato,
   invece di provare a stampare l'oggetto intero (che fa crashare React).

   Nomi ufficiali dei blocchi (21/09, FRD Knowledge Management v1.0 pag.
   6): Blocco A "Coordinamento e Governance", Blocco B "Analisi, Soluzioni
   e Processi", Blocco C "Valore, Metriche e Posizionamento" — ciascuno
   attivato da una domanda filtro specifica nel questionario ricorrente
   (SA-2b), non dedotto dal contenuto.

   Campi che il codice precedente si aspettava e NON esistono qui:
   titolo, risorsa_nome, referente_hr, utilizzabile (era "marcata_
   utilizzabile"), ultima_compilazione (era "ultima_compilazione_ts"),
   marcatura_autore/marcatura_nota (solo "nota_sales" esiste, senza
   autore), compilazioni (l'array con tipo/data/risposte non c'è).

   PATCH /schede/{id}: nome campo versione per il controllo di
   concorrenza non confermato con certezza (il modello ModificaSchedaIn
   non è nel collaudo con i nomi esatti) — uso "versione" (coerente con
   quanto la GET restituisce) al posto di "version" usato prima, ma resta
   un'ipotesi da verificare se il 409 non si comporta come previsto.

   POST /schede/{id}/sales-mark: MarcaturaSalesIn è { utilizzabile: bool,
   nota?: string } — confermato da Specifica §8.1. Il campo "autore" che
   il codice mandava non esiste nel modello: tolto, non veniva comunque
   restituito da nessuna parte nella risposta (solo nota_sales, senza
   autore).

   Integrato dal collaudo aggiornato del 18/09 (Collaudo_KM_TalentGPT_
   20260907_3.pdf, Appendice F.2): blocco_a_attivato/b_attivato/c_attivato
   (booleani, rispondono alle domande filtro del questionario — più
   affidabili del semplice controllo "c'è contenuto?" usato prima) e
   scheda.soddisfazione.{voto,commento} (compilata dal consulente in
   chiusura, questionario AF sezione Z, mai mostrata prima). */

/* 16/10 — FE-06 (SIT-100): niente valori tecnici a schermo. Etichette
   leggibili per i campi dei blocchi, date in formato italiano, stato senza
   codice E1/E2, presenza ISA e citabilità con il nome (valori ammessi dalla
   migration 045), elenchi come testo e non come JSON. */
const ETICHETTE_CAMPI = {
  attivita_deliverable: 'Attività e deliverable',
  strumenti_tecnologie: 'Strumenti e tecnologie',
  difficolta_soluzioni: 'Difficoltà e soluzioni',
  team_figure: 'Team e figure professionali',
  apprendimenti: 'Apprendimenti',
  coordinamento: 'Coordinamento',
  rischi_criticita: 'Rischi e criticità',
  reporting_escalation: 'Reporting ed escalation',
  bisogni_gap: 'Bisogni e gap',
  soluzione_proposta: 'Soluzione proposta',
  processi_impattati: 'Processi impattati',
  valore_prodotto: 'Valore prodotto',
  metriche_kpi: 'Metriche e KPI',
  lesson_learned: 'Lesson learned',
  storytelling_commerciale: 'Elementi per il racconto commerciale',
};

function labelFromKey(key) {
  if (ETICHETTE_CAMPI[key]) return ETICHETTE_CAMPI[key];
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// Tassonomia presenza ISA (migration 045, tassonomia_presenza_isa).
const PRESENZA_ISA_LABELS = {
  contraente_diretto: 'Contraente diretto',
  mandataria_rti: 'Mandataria di RTI',
  mandante_rti: 'Mandante di RTI',
  subfornitore: 'Subfornitore di main contractor/SI',
  fornitura_risorse: 'Fornitura di risorse professionali',
};

// «2026-10-14T09:54:23.875562Z» → «14/10/2026 alle 11:54» (ora locale).
function formatDataOraIt(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  const giorno = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const ora = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  return `${giorno} alle ${ora}`;
}

// Elenchi come «SAP, CRM», oggetti come «chiave: valore»; mai JSON grezzo.
function testoValore(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(testoValore).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([k, v]) => `${labelFromKey(k)}: ${testoValore(v)}`)
      .join('; ');
  }
  return String(value);
}

// 16/10 — UC-KM-03 / V39: autore della marcatura. Il backend salva
// l'identificativo dell'utente (marcata_da): se è numerico si mostra
// «utente #N», come per l'ultimo aggiornamento HR dell'anagrafica.
function etichettaAutore(a) {
  if (a === null || a === undefined || a === '') return null;
  return /^\d+$/.test(String(a)) ? `utente #${a}` : String(a);
}

const maiuscolaIniziale = (t) => (t ? String(t).charAt(0).toUpperCase() + String(t).slice(1) : t);

// FRD KM v1.0 §4.3: "citabile liberamente, citabile con anonimizzazione del
// cliente, non citabile". Valori esatti non confermati dal collaudo tecnico
// (che conferma solo il nome campo "citabilita") — se il backend usa stringhe
// diverse, questa mappa va corretta, ma nel frattempo mostra comunque il
// valore grezzo invece di nascondere l'informazione.
const CITABILITA_LABELS = {
  libera: 'Citabile liberamente',
  anonimizzata: 'Citabile con anonimizzazione',
  non_citabile: 'Non citabile',
};

// Testo delle domande che generano ciascun campo — dal catalogo reale delle
// domande (catalogo-domande.json, sezioni G e H). Verificato il 21/09 con
// un confronto incrociato diretto contro SA-2b.json (file indipendente,
// stessa struttura Q-xxx): testo identico carattere per carattere su
// Q-106/107/108/109 — buona conferma che entrambi discendono dalla stessa
// fonte originale (il foglio Excel che l'FRD cita come riferimento
// ufficiale), non una ricostruzione nostra dal solo testo dell'FRD.
const DOMANDA_PER_CAMPO = {
  attivita_deliverable: 'Attività svolte e deliverable prodotti nel periodo',
  strumenti_tecnologie: 'Strumenti e tecnologie utilizzati concretamente nel lavoro operativo',
  difficolta_soluzioni: 'Difficoltà operative incontrate e modalità di risoluzione',
  team_figure: 'Team e figure professionali con cui si è collaborato nel periodo',
  apprendimenti: 'Apprendimenti maturati nel periodo',
  coordinamento: 'Struttura del coordinamento tra i soggetti coinvolti',
  rischi_criticita: 'Rischi o criticità di programma gestiti e relative modalità',
  reporting_escalation: 'Meccanismi di reporting o escalation in uso',
  bisogni_gap: 'Bisogni di business o gap identificati',
  soluzione_proposta: 'Soluzione proposta e ragioni della sua efficacia',
  processi_impattati: 'Processi funzionali impattati e misura dell\u2019impatto',
  valore_prodotto: 'Valore concreto prodotto per il cliente finale',
  metriche_kpi: 'Metriche o KPI che ne danno evidenza',
  lesson_learned: 'Lesson learned strategiche riutilizzabili su un progetto analogo',
  storytelling_commerciale: 'Elementi da mettere in evidenza raccontando l\u2019esperienza a un potenziale cliente',
};

function renderBlocco(blocco) {
  if (!blocco) return null;
  if (typeof blocco === 'string') return <p style={{ margin: 0 }}>{blocco}</p>;
  if (typeof blocco === 'object') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {Object.entries(blocco).map(([key, value]) => (
          <div key={key}>
            {DOMANDA_PER_CAMPO[key] && (
              <p style={{ margin: '0 0 2px', fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-faint, var(--color-ink-muted))', fontStyle: 'italic' }}>
                {DOMANDA_PER_CAMPO[key]}
              </p>
            )}
            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-ink-muted)' }}>
              {labelFromKey(key)}
            </span>
            <p style={{ margin: 0 }}>{testoValore(value)}</p>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

/* Modifica inline di un blocco della scheda (UC-KM-02: «i contenuti dei
   questionari sono presentati in sola lettura, ma un pulsante apposito deve
   poter permettere la modifica inline»). PATCH /schede/{id} con il blocco
   intero: il backend crea la versione successiva e archivia la precedente. */
function BloccoModificabile({ titolo, chiave, blocco, puoModificare, onSalva }) {
  const [aperto, setAperto] = useState(false);
  const [bozza, setBozza] = useState({});
  const [salvando, setSalvando] = useState(false);
  const eOggetto = blocco && typeof blocco === 'object';

  function apri() {
    setBozza(eOggetto ? Object.fromEntries(Object.entries(blocco).map(([k, v]) => [k, typeof v === 'string' ? v : JSON.stringify(v)])) : {});
    setAperto(true);
  }

  async function salva() {
    setSalvando(true);
    const ok = await onSalva(chiave, bozza);
    setSalvando(false);
    if (ok) setAperto(false);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-2)' }}>
        <dt className="field-label">{titolo}</dt>
        {puoModificare && eOggetto && !aperto && (
          <button type="button" className="btn-link" style={{ fontSize: 12 }} onClick={apri}>Modifica</button>
        )}
      </div>
      {!aperto ? renderBlocco(blocco) : (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {Object.keys(bozza).map((k) => (
            <label key={k} style={{ display: 'grid', gap: 2 }}>
              <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-ink-muted)' }}>{labelFromKey(k)}</span>
              <textarea className="field-textarea" rows={3} value={bozza[k]} onChange={(e) => setBozza((b) => ({ ...b, [k]: e.target.value }))} />
            </label>
          ))}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button type="button" className="btn btn-primary" disabled={salvando} onClick={salva}>{salvando ? 'Salvataggio…' : 'Salva'}</button>
            <button type="button" className="btn btn-secondary" disabled={salvando} onClick={() => setAperto(false)}>Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SchedaDetailPage() {
  const { schedaId } = useParams();
  const kmClient = useKmClient('km');
  // Ruolo vero da /users/me, non il simulatore KM (lib/auth.jsx, fissato
  // ad ADMIN per tutti dal 28/09 — decisione di progetto): la UI deve
  // mostrare solo ciò che il ruolo reale consente, coerentemente con
  // require_hr/require_sales lato backend.
  const { user: realUser } = useRealAuth();
  const role = realUser?.role;
  const isHrReale = ['hr', 'admin'].includes(role);
  // 16/10 — UC-KM-02: il livello 4 (storico delle compilazioni e risposte
  // originali, storico delle marcature) è consultabile anche dai ruoli
  // commerciali. Il backend lo consente dal 15/10 (require_interno).
  const isInterno = ['hr', 'admin', 'sales', 'management'].includes(role);
  const [editText, setEditText] = useState('');
  const [editing, setEditing] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [conflict, setConflict] = useState(false);
  // Aggiunto 24/09: mostra le risposte grezze (domanda per domanda) di
  // questa esperienza, accanto al contenuto già rielaborato dai blocchi.
  const [risposteAperte, setRisposteAperte] = useState(false);
  const [markNote, setMarkNote] = useState('');
  // UC-KM-03 passi 3-5: prima la scelta, poi la conferma (o l'annullamento).
  const [sceltaMarca, setSceltaMarca] = useState(null); // null | true | false
  // V39: storico delle marcature (livello 4), da GET /schede/{id}/marcature.
  const [storicoMarcature, setStoricoMarcature] = useState(null);
  const [tickStorico, setTickStorico] = useState(0);
  // UAT-15: esito della marcatura mostrato accanto ai pulsanti (prima il tag
  // di stato era in alto e bisognava scorrere per vedere l'effetto del clic).
  const [marking, setMarking] = useState(false);
  const [markFeedback, setMarkFeedback] = useState(null);
  const [markError, setMarkError] = useState(null);

  const { data: scheda, status, error, refetch } = useKmQuery(
    () => kmClient.get(`/schede/${schedaId}`),
    [schedaId]
  );
  // Intestazione (primo livello di UC-KM-02): i campi strutturati e
  // filtrabili stanno sull'esperienza, non sulla scheda.
  const { data: esp } = useKmQuery(
    () => (scheda?.esperienza_id ? kmClient.get(`/esperienze/${scheda.esperienza_id}`) : Promise.resolve(null)),
    [scheda?.esperienza_id]
  );
  const { filtri } = useFiltriEsperienze();

  // V39: storico delle marcature, ricaricato dopo ogni nuova marcatura.
  React.useEffect(() => {
    if (!isInterno || !schedaId) return undefined;
    let annullato = false;
    kmClient.get(`/schede/${schedaId}/marcature`)
      .then((d) => { if (!annullato) setStoricoMarcature(Array.isArray(d?.marcature) ? d.marcature : []); })
      .catch(() => { if (!annullato) setStoricoMarcature(null); });
    return () => { annullato = true; };
  }, [kmClient, schedaId, isInterno, tickStorico]);

  if (status === 'loading') {
    return <div className="card"><div className="skeleton-line" style={{ width: '60%' }} /></div>;
  }
  if (status === 'error') {
    return (
      <div className="state-error">
        {error?.status === 404 ? 'Scheda non trovata.' : 'Errore nel caricamento.'}
        <ErrorDetails error={error} />
      </div>
    );
  }

  async function handleSaveEdit() {
    if (!editText.trim()) return;
    setEditing(true);
    setSaveError(null);
    setConflict(false);
    try {
      await kmClient.patch(`/schede/${schedaId}`, { sommario: editText, versione: scheda.versione });
      setEditText('');
      refetch();
    } catch (err) {
      if (err?.status === 409) {
        // Versione non più corrente: ricaricare senza sovrascrivere (§5.6)
        setConflict(true);
        refetch();
      } else {
        setSaveError(err?.detail || 'Salvataggio non riuscito.');
      }
    } finally {
      setEditing(false);
    }
  }

  async function handleMark(usabile) {
    setMarking(true);
    setMarkFeedback(null);
    setMarkError(null);
    try {
      await kmClient.post(`/schede/${schedaId}/sales-mark`, {
        utilizzabile: usabile,
        nota: markNote || undefined,
      });
      setMarkNote('');
      setSceltaMarca(null);
      setTickStorico((t) => t + 1);
      setMarkFeedback(usabile
        ? 'Scheda marcata come utilizzabile commercialmente.'
        : 'Scheda marcata come non utilizzabile commercialmente.');
      refetch();
    } catch (err) {
      setMarkError(err?.detail || 'Marcatura non riuscita.');
    } finally {
      setMarking(false);
    }
  }

  async function salvaBlocco(chiave, valore) {
    setSaveError(null);
    setConflict(false);
    try {
      await kmClient.patch(`/schede/${schedaId}`, { [chiave]: valore, versione: scheda.versione });
      refetch();
      return true;
    } catch (err) {
      if (err?.status === 409) { setConflict(true); refetch(); }
      else setSaveError(err?.detail || 'Salvataggio non riuscito.');
      return false;
    }
  }


  const canEdit = canEditScheda(role);
  const canMark = canSalesMark(role);


  // Blocco "attivo" (Collaudo 20260907_3.pdf, Appendice F.2): i flag
  // blocco_*_attivato sono la fonte corretta — rispondono alle domande
  // filtro del questionario (Q-106/110/114), non alla presenza di
  // contenuto. Se il flag non arriva (risposta più vecchia, o backend
  // che non lo espone ancora), ripiego sul controllo di contenuto usato
  // prima, invece di nascondere tutto.
  const blocchiAttivi = {
    a: scheda?.blocco_a_attivato ?? Boolean(scheda?.blocco_a),
    b: scheda?.blocco_b_attivato ?? Boolean(scheda?.blocco_b),
    c: scheda?.blocco_c_attivato ?? Boolean(scheda?.blocco_c),
  };

  return (
    <div>
      {/* UAT-28 (12/10): dall'elenco esperienze il clic sulla riga apre la
          scheda, mentre data di chiusura prevista e chiusura (UC-P03-02/03)
          stanno nel dettaglio dell'esperienza, raggiungibile solo dal
          link «Esperienza →» o da «Esperienze in scadenza». HR lo ritrova
          qui finché l'esperienza è in corso; Sales non chiude esperienze.
          12/10 — il collegamento è il tasto «Gestisci» accanto al titolo
          (prima una riga di testo sotto il titolo). */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-3)', marginBottom: 'var(--space-1)' }}>
        <h1 className="section-title" style={{ margin: 0 }}>Scheda esperienza</h1>
        {isHrReale && scheda?.esperienza_id && (esp?.se_011_stato || scheda?.stato_esperienza) !== 'chiusa' && (
          <Link
            to={`/knowledge-management/esperienze/${scheda.esperienza_id}`}
            className="btn btn-secondary"
            title="Data di chiusura prevista e chiusura dell'esperienza"
          >
            Gestisci
          </Link>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-5)' }}>
        <p style={{ color: 'var(--color-ink-muted)', margin: 0 }}>
          {esp?.denominazione_progetto || 'Progetto'}{' · '}{esp?.cliente_finale || esp?.se_002_cliente || 'Cliente non disponibile'}
          {' · '}versione {scheda?.versione ?? '—'}
        </p>
        {isInterno && (
          <button
            type="button"
            className="btn-link"
            style={{ fontSize: 12.5 }}
            onClick={() => setRisposteAperte(true)}
            title="Domande e risposte grezze, non rielaborate — tutte le compilazioni"
          >
            Storico compilazioni e risposte originali →
          </button>
        )}
      </div>

      {conflict && (
        <p className="badge badge-warning" style={{ marginBottom: 'var(--space-4)' }}>
          La scheda è stata aggiornata da un altro utente: la versione corrente è stata ricaricata.
        </p>
      )}
      {saveError && <p className="state-error" style={{ textAlign: 'left', padding: 0, marginBottom: 'var(--space-4)' }}>{saveError}</p>}

      {esp && (
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>Intestazione</h2>
          <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-3)', margin: 0 }}>
            {[
              ['Risorsa', esp.se_001_consulente],
              ['Ruolo', esp.se_006_ruolo || scheda?.ruolo],
              ['Seniority', maiuscolaIniziale(esp.seniority)],
              ['Settore', [nomeDaCodice(filtri?.settori, esp.settore_l1), nomeDaCodice(filtri?.settori, esp.settore_l2)].filter(Boolean).join(' · ')],
              ['Dominio funzionale', [nomeDaCodice(filtri?.domini, esp.dominio_l1), nomeDaCodice(filtri?.domini, esp.dominio_l2)].filter(Boolean).join(' · ')],
              ['Periodo', esp.se_007_periodo],
              ['Stato', (esp.se_011_stato || scheda?.stato_esperienza) === 'chiusa' ? 'Chiusa' : 'In corso'],
              ['Capacità garantita', esp.capacita_garantita != null ? `${Number(esp.capacita_garantita)}%` : null],
              ['Referente commerciale', esp.referente_commerciale],
              ['Data chiusura prevista', esp.data_chiusura_prevista ? formatDataIt(esp.data_chiusura_prevista) : null],
              ['Certificazioni', Array.isArray(esp.certificazioni) ? esp.certificazioni.join(', ') : null],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="field-label">{k}</dt>
                <dd style={{ margin: 0 }}>{v || '—'}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-3)', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {(() => {
              const u = etichettaUtilizzabile(scheda?.marcata_utilizzabile);
              return <span className={`badge ${u.classe}`}>{u.testo}</span>;
            })()}
            {/* Citabilità: 3 valori secondo FRD KM v1.0 §4.3/§4.4 — campo di
                competenza del Referente Commerciale, mai mostrato prima
                d'ora. Nome campo "citabilita" confermato nel collaudo
                (§6.9), ma non i suoi valori esatti: mappo quelli descritti
                nell'FRD, da verificare se coincidono coi valori reali. */}
            {scheda?.citabilita && (
              <span className="badge badge-neutral">
                {CITABILITA_LABELS[scheda.citabilita] || maiuscolaIniziale(String(scheda.citabilita).replace(/_/g, ' '))}
              </span>
            )}
            {scheda?.presenza_isa != null && (
              <span className="badge badge-neutral">Presenza ISA: {PRESENZA_ISA_LABELS[scheda.presenza_isa] || maiuscolaIniziale(String(scheda.presenza_isa).replace(/_/g, ' '))}</span>
            )}
          </div>
          <span style={{ color: 'var(--color-ink-muted)', fontSize: 'var(--font-size-sm)' }}>
            Ultimo aggiornamento: {formatDataOraIt(scheda?.ultima_compilazione_ts) || 'non disponibile'}
            {scheda?.ultima_compilazione_tipo ? ` (${scheda.ultima_compilazione_tipo})` : ''}
          </span>
        </div>
        <p>{scheda?.sommario || 'Sommario non ancora disponibile.'}</p>
        {scheda?.marcata_utilizzabile != null && scheda?.nota_sales && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', marginBottom: 0 }}>
            Nota commerciale: "{scheda.nota_sales}"
          </p>
        )}
        {/* 16/10 — V39 (migration 045): data della marcatura e nota di
            citabilità, ora salvate dal backend. */}
        {scheda?.marcata_utilizzabile != null && scheda?.marcata_il && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', marginBottom: 0 }}>
            Marcatura del {formatDataOraIt(scheda.marcata_il)}
            {etichettaAutore(scheda?.marcata_da) ? `, di ${etichettaAutore(scheda.marcata_da)}` : ''}
          </p>
        )}
        {scheda?.nota_citabilita && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', marginBottom: 0 }}>
            Nota sulla citabilità: "{scheda.nota_citabilita}"
          </p>
        )}
        {Array.isArray(scheda?.tecnologie_unificate) && scheda.tecnologie_unificate.length > 0 && (
          <div style={{ marginTop: 'var(--space-3)', display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {scheda.tecnologie_unificate.map((t) => (
              <span key={t} className="badge badge-neutral">{t}</span>
            ))}
          </div>
        )}
      </div>

      {/* Soddisfazione (Collaudo 20260907_3.pdf, Appendice F.2): compilata
          dal consulente in chiusura (questionario AF sezione Z) — voto +
          commento libero. Mai mostrata prima d'ora. */}
      {scheda?.soddisfazione && (scheda.soddisfazione.voto != null || scheda.soddisfazione.commento) && (
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>Soddisfazione del consulente</h2>
          {scheda.soddisfazione.voto != null && (
            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>{scheda.soddisfazione.voto}</div>
          )}
          {scheda.soddisfazione.commento && (
            <p style={{ margin: 'var(--space-1) 0 0' }}>{scheda.soddisfazione.commento}</p>
          )}
        </div>
      )}

      {scheda?.nucleo_comune && (
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>Nucleo comune</h2>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', marginTop: 0, marginBottom: 'var(--space-3)' }}>
            Parte sempre presente del questionario ricorrente — attività, strumenti, difficoltà, team, apprendimenti. A differenza dei blocchi sotto, non richiede attivazione.
          </p>
          <BloccoModificabile titolo="Contenuti" chiave="nucleo_comune" blocco={scheda.nucleo_comune} puoModificare={canEdit} onSalva={salvaBlocco} />
        </div>
      )}

      {(blocchiAttivi.a || blocchiAttivi.b || blocchiAttivi.c) && (
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>Blocchi scheda</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {blocchiAttivi.a && <BloccoModificabile titolo="Blocco A — Coordinamento e Governance" chiave="blocco_a" blocco={scheda.blocco_a} puoModificare={canEdit} onSalva={salvaBlocco} />}
            {blocchiAttivi.b && <BloccoModificabile titolo="Blocco B — Analisi, Soluzioni e Processi" chiave="blocco_b" blocco={scheda.blocco_b} puoModificare={canEdit} onSalva={salvaBlocco} />}
            {blocchiAttivi.c && <BloccoModificabile titolo="Blocco C — Valore, Metriche e Posizionamento" chiave="blocco_c" blocco={scheda.blocco_c} puoModificare={canEdit} onSalva={salvaBlocco} />}
          </div>
        </div>
      )}

      {risposteAperte && (
        <RisposteSelfAssessmentModal
          resourceId={scheda?.esperienza_id}
          nome={scheda?.titolo || 'questa esperienza'}
          tipo="esperienza"
          onClose={() => setRisposteAperte(false)}
        />
      )}

      {!canEdit && !canMark && (
        <div className="card state-empty">
          Il tuo ruolo attuale ha accesso in sola consultazione a questa scheda
          (Specifica v1.1 §4.2).
        </div>
      )}

      {canEdit && (
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>Modifica del sommario integrato</h2>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', marginTop: 0 }}>
            Il sommario è rigenerato automaticamente a ogni nuova compilazione, senza stati di validazione (V15). Una modifica manuale crea una nuova versione della scheda.
          </p>
          {/* FRD KM v1.0 §5.2 attribuisce la validazione del sommario al
              consulente, non a HR/Sales. RISOLTO 18/09: confermato da
              Daniele Turra — "HR fa tutto, per ora". */}
          <textarea
            className="field-textarea"
            rows={4}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            placeholder="Aggiorna il sommario…"
          />
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: 'var(--space-3)' }}
            disabled={!editText.trim() || editing}
            onClick={handleSaveEdit}
          >
            {editing ? 'Salvataggio…' : 'Salva nuova versione'}
          </button>
        </div>
      )}

      {canMark && (
        <div className="card">
          <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>Usabilità commerciale</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-ink-muted)' }}>Stato attuale:</span>
            {(() => {
              const u = etichettaUtilizzabile(scheda?.marcata_utilizzabile);
              return <span className={`badge ${u.classe}`}>{u.testo}</span>;
            })()}
          </div>
          {/* 16/10 — UC-KM-03: scelta del valore, nota facoltativa, poi
              conferma; «Annulla» non salva nulla (flusso alternativo 1). */}
          <div role="radiogroup" aria-label="Nuova marcatura" style={{ display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-3)', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="radio" name="marcatura" checked={sceltaMarca === true} onChange={() => { setSceltaMarca(true); setMarkFeedback(null); }} disabled={marking} />
              Utilizzabile
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="radio" name="marcatura" checked={sceltaMarca === false} onChange={() => { setSceltaMarca(false); setMarkFeedback(null); }} disabled={marking} />
              Non utilizzabile
            </label>
          </div>
          {sceltaMarca !== null && (
            <>
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <input
                  className="field-input"
                  placeholder="Nota (facoltativa): motivo del giudizio o contesto di utilizzo"
                  value={markNote}
                  onChange={(e) => setMarkNote(e.target.value)}
                />
              </div>
              <button type="button" className="btn btn-accent" disabled={marking} onClick={() => handleMark(sceltaMarca)}>
                Conferma marcatura
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ marginLeft: 'var(--space-3)' }}
                disabled={marking}
                onClick={() => { setSceltaMarca(null); setMarkNote(''); setMarkError(null); }}
              >
                Annulla
              </button>
            </>
          )}
          {marking && <p role="status" style={{ margin: 'var(--space-3) 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--color-ink-muted)' }}>Salvataggio…</p>}
          {markFeedback && <p role="status" className="badge badge-success" style={{ margin: 'var(--space-3) 0 0' }}>{markFeedback}</p>}
          {markError && <p role="alert" className="state-error" style={{ textAlign: 'left', padding: 0, margin: 'var(--space-3) 0 0' }}>{markError}</p>}
        </div>
      )}

      {/* 16/10 — V39: storico append-only delle marcature, nel livello 4. */}
      {isInterno && Array.isArray(storicoMarcature) && (
        <div className="card" style={{ marginTop: 'var(--space-4)' }}>
          <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>Storico delle marcature</h2>
          {storicoMarcature.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--color-ink-muted)', fontSize: 'var(--font-size-sm)' }}>Nessuna marcatura registrata.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
                <thead>
                  <tr>
                    {['Data', 'Marcatura', 'Nota', 'Autore'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', borderBottom: '1px solid var(--color-border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {storicoMarcature.map((m, i) => {
                    const valore = m.utilizzabile ?? m.marcata_utilizzabile ?? m.valore;
                    const u = etichettaUtilizzabile(valore);
                    return (
                      <tr key={m.id ?? i}>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--color-border)' }}>{formatDataOraIt(m.marcata_il ?? m.created_at ?? m.data) || '—'}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}><span className={`badge ${u.classe}`}>{u.testo}</span></td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>{m.nota ?? m.nota_sales ?? '—'}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--color-border)' }}>{etichettaAutore(m.autore_nome ?? m.autore ?? m.marcata_da ?? m.user_ref) || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
